import React, { useContext, useEffect, useState } from "react";
import Table from "./Table";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import { useTranslation } from "react-i18next";
import apiPath from "utils/apiPath";
import dayjs from "dayjs";
import { apiGet, apiDelete } from "utils/apiFetch"; // Add apiDelete import
import AuthContext from "context/AuthContext";
import Pagination from "../Pagination";
import CountUp from "react-countup";
import helpers from "utils/helpers";
import OSearch from "components/reusable/OSearch";
import useToastContext from "hooks/useToastContext";

function EarningManager() {
  const { t } = useTranslation();
  const { logoutUser, updatePageName, user } = useContext(AuthContext);
  const manager = user?.permission?.find((e) => e.manager === "earning-managers") ?? {};
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });
  const [earningManager, setEarningManager] = useState([]);
  const [totalEarning, setTotalEarning] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // Add loading state
  const [filterData, setFilterData] = useState({
    searchKey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const notification = useToastContext();

  const handleApiError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 409) {
      logoutUser();
    }
  };

  useEffect(() => {
    updatePageName(t("NAV_EARNING_MANAGER"));
  }, []);

  const getEarning = async (data) => {
    try {
      const { startDate, endDate, searchKey, isFilter } = filterData;
      if ((data?.deletePage && earningManager?.length >= 1) || (isFilter && data?.statusChange && earningManager?.length >= 1)) {
        setPage(page - 1);
        setPaginationObj({ ...paginationObj, page: page - 1 });
      }
      const payload = {
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format("YYYY-MM-DD"), null),
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format("YYYY-MM-DD"), null),
        pageSize,
        page,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
        keyword: searchKey?.trim(),
      };
      const path = apiPath.getEarnings;
      const result = await apiGet(path, payload);
      if (result?.status === 200) {
        const response = result?.data?.results;
        const resultStatus = result?.data?.success;

        setEarningManager(response?.docs);
        setTotalEarning(response?.totalEarning);
        setPaginationObj({
          page: helpers?.ternaryCondition(resultStatus, response.page, null),
          pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
          perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
          totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
        });
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Add delete function
  const handleDelete = async (transactionId) => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      const path = `${apiPath.deleteEarning}/${transactionId}`; // Assuming you have deleteEarning in apiPath
      const result = await apiDelete(path);
      
      if (result?.status === 200) {
        notification.success(t("TRANSACTION_DELETED_SUCCESSFULLY"));
        // Refresh the list after deletion
        getEarning({ deletePage: true });
      } else {
        notification.error(result?.data?.message || t("DELETE_FAILED"));
      }
    } catch (error) {
      handleApiError(error);
      notification.error(error?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    getEarning();
  }, [page, filterData, sort]);

  const handleDateChange = (start, end) => {
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
    setPage(1);
  };

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    } else if (searchTerm || !filterData?.isReset) {
      setFilterData({
        ...filterData,
        isReset: false,
        searchKey: debouncedSearchTerm ? debouncedSearchTerm : "",
        isFilter: debouncedSearchTerm ? true : false,
      });
      setPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const handleReset = () => {
    setFilterData({
      searchKey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
    setSearchTerm("");
  };
  
  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  return (
    <div>
      <div className="bg-[#F9F9F9]">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] rounded-lg">
            <form className="border-b border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 py-3">
              <div className="flex flex-wrap col-span-2 items-center">
                <div className="lg:pt-0 pt-3 flex-wrap flex items-center mb-2">
                  <div className="mb-3 relative flex items-center w-[340px]">
                    <OSearch setSearchTerm={setSearchTerm} searchTerm={searchTerm} placeholder={t("SEARCH_BY_COMPANY_NAME_TRANSACTION_ID_SUBSCRIPTION_ID")} />
                  </div>
                  <ODateRangePicker isReset={filterData?.isReset} setIsReset={setFilterData} handleDateChange={handleDateChange} />
                  <button type="button" className="bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2" onClick={handleReset}>
                    {t("O_RESET")}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end px-4 ms-auto">
                <label htmlFor="default-search" className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
                  {t("O_SEARCH")}
                </label>
                <button type="button" className=" text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent hover:bg-DarkBlue sm:w-auto w-1/2" style={{ background: "#e7e7e7", color: "#000" }}>
                  Total earning till date: $
                  <CountUp duration={0.6} decimals={2} end={totalEarning} />
                </button>
              </div>
            </form>
            <Table 
              earningManager={earningManager} 
              page={page} 
              setSort={setSort} 
              sort={sort} 
              manager={manager}
              onDelete={handleDelete} // Pass delete function
            />
            {paginationObj?.totalItems ? (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                page={page}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EarningManager;





// import React, { useContext, useEffect, useState } from "react";
// import Table from "./Table";
// import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
// import { useTranslation } from "react-i18next";
// import apiPath from "utils/apiPath";
// import dayjs from "dayjs";
// import { apiGet } from "utils/apiFetch";
// import AuthContext from "context/AuthContext";
// import Pagination from "../Pagination";
// import CountUp from "react-countup";
// import helpers from "utils/helpers";
// import OSearch from "components/reusable/OSearch";

// function EarningManager() {
//   const { t } = useTranslation();
//   const { logoutUser, updatePageName, user } = useContext(AuthContext);
//   const manager = user?.permission?.find((e) => e.manager === "earning-managers") ?? {};
//   const [paginationObj, setPaginationObj] = useState({
//     page: 1,
//     pageCount: 1,
//     pageRangeDisplayed: 10,
//   });
//   const [page, setPage] = useState(1);
//   const [pageSize] = useState(10);
//   const [sort, setSort] = useState({
//     sortBy: "createdAt",
//     sortType: "desc",
//   });
//   const [earningManager, setEarningManager] = useState([]);
//   const [totalEarning, setTotalEarning] = useState(0);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
//   const [isInitialized, setIsInitialized] = useState(false);
//   const [filterData, setFilterData] = useState({
//     searchKey: "",
//     startDate: "",
//     endDate: "",
//     isReset: false,
//     isFilter: false,
//   });

//   const handleApiError = (error) => {
//     if (error.response?.status === 401 || error.response?.status === 409) {
//       logoutUser();
//     }
//   };

//   useEffect(() => {
//     updatePageName(t("NAV_EARNING_MANAGER"));
//   }, []);

//   const getEarning = async (data) => {
//     try {
//       const { startDate, endDate, searchKey, isFilter } = filterData;
//       if ((data?.deletePage && earningManager?.length >= 1) || (isFilter && data?.statusChange && earningManager?.length >= 1)) {
//         setPage(page - 1);
//         setPaginationObj({ ...paginationObj, page: page - 1 });
//       }
//       const payload = {
//         startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format("YYYY-MM-DD"), null),
//         endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format("YYYY-MM-DD"), null),
//         pageSize,
//         page,
//         sortBy: sort.sortBy,
//         sortType: sort.sortType,
//         keyword: searchKey?.trim(),
//       };
//       const path = apiPath.getEarnings;
//       const result = await apiGet(path, payload);
//       if (result?.status === 200) {
//         const response = result?.data?.results;
//         const resultStatus = result?.data?.success;

//         setEarningManager(response?.docs);
//         setTotalEarning(response?.totalEarning);
//         setPaginationObj({
//           page: helpers?.ternaryCondition(resultStatus, response.page, null),
//           pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
//           perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
//           totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
//         });
//       }
//     } catch (error) {
//       handleApiError(error);
//     }
//   };
//   useEffect(() => {
//     getEarning();
//   }, [page, filterData, sort]);

//   const handleDateChange = (start, end) => {
//     setFilterData({
//       ...filterData,
//       startDate: start,
//       endDate: end,
//       isFilter: true,
//     });
//     setPage(1);
//   };

//   useEffect(() => {
//     if (!isInitialized) {
//       setIsInitialized(true);
//     } else if (searchTerm || !filterData?.isReset) {
//       setFilterData({
//         ...filterData,
//         isReset: false,
//         searchKey: debouncedSearchTerm ? debouncedSearchTerm : "",
//         isFilter: debouncedSearchTerm ? true : false,
//       });
//       setPage(1);
//     }
//   }, [debouncedSearchTerm]);

//   useEffect(() => {
//     const timeoutId = setTimeout(() => {
//       setDebouncedSearchTerm(searchTerm);
//     }, 500);
//     return () => {
//       clearTimeout(timeoutId);
//     };
//   }, [searchTerm]);

//   const handleReset = () => {
//     setFilterData({
//       searchKey: "",
//       startDate: "",
//       endDate: "",
//       isReset: true,
//       isFilter: false,
//     });
//     setPage(1);
//     setSearchTerm("");
//   };
//   const handlePageClick = (event) => {
//     const newPage = event.selected + 1;
//     setPage(newPage);
//   };

//   return (
//     <div>
//       <div className="bg-[#F9F9F9]">
//         <div className="px-3 py-4">
//           <div className="bg-white border border-[#E9EDF9] rounded-lg">
//             <form className="border-b border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4 py-3">
//               <div className="flex flex-wrap col-span-2 items-center">
//                 <div className="lg:pt-0 pt-3 flex-wrap flex items-center mb-2">
//                   <div className="mb-3 relative flex items-center w-[340px]">
//                     <OSearch setSearchTerm={setSearchTerm} searchTerm={searchTerm} placeholder={t("SEARCH_BY_COMPANY_NAME_TRANSACTION_ID_SUBSCRIPTION_ID")} />
//                   </div>
//                   <ODateRangePicker isReset={filterData?.isReset} setIsReset={setFilterData} handleDateChange={handleDateChange} />
//                   <button type="button" className="bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2" onClick={handleReset}>
//                     {t("O_RESET")}
//                   </button>
//                 </div>
//               </div>
//               <div className="flex items-center justify-end px-4 ms-auto">
//                 <label htmlFor="default-search" className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
//                   {t("O_SEARCH")}
//                 </label>
//                 <button type="button" className=" text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent hover:bg-DarkBlue sm:w-auto w-1/2" style={{ background: "#e7e7e7", color: "#000" }}>
//                   Total earning till date: $
//                   <CountUp duration={0.6} decimals={2} end={totalEarning} />
//                 </button>
//               </div>
//             </form>
//             <Table earningManager={earningManager} page={page} setSort={setSort} sort={sort} manager={manager} />
//             {paginationObj?.totalItems ? (
//               <Pagination
//                 handlePageClick={handlePageClick}
//                 options={paginationObj}
//                 // isDelete={isDelete}
//                 page={page}
//               />
//             ) : null}
//             {/* <div className='md:flex md:justify-end'>
//               <p className='-space-x-px pt-5 md:pb-5 pr-5 text-gray-500'>{t('O_TOTAL')} {paginationObj?.totalItems} {t('O_RECORDS')}</p>
//               <nav aria-label='Page navigation example'>
//                 <ReactPaginate
//                   breakLabel='...'
//                   nextLabel={t('O_NEXT')}
//                   onPageChange={handlePageClick}
//                   pageRangeDisplayed={paginationObj.pageRangeDisplayed}
//                   pageCount={paginationObj.pageCount}
//                   previousLabel={t('O_PREVIOUS')}
//                   renderOnZeroPageCount='null'
//                   className='inline-flex -space-x-px pt-5 pb-5 pr-5 flex-wrap justify-center'
//                   activeClassName='bg-blue-50'
//                   previousLinkClassName='px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
//                   nextLinkClassName='px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
//                   pageLinkClassName='px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
//                   activeLinkClassName='4 px-3 py-2 text-blue-600 border border-gray-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white'
//                 />
//               </nav>
//             </div> */}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default EarningManager;
